import { LinkOff as LinkOffIcon } from 'components/icons';
import IconButton from 'components/@extended/IconButton';
import StatusChip from 'components/@extended/StatusChip';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { RecordLink } from 'components/record-chip/RecordLink';
import { Tooltip } from 'components/ui/tooltip';
import { useCallback } from 'react';
import { useUnlinkConnectedCaseMutation } from 'store/api/matters';
import { ClassifierData } from 'types/classifiers';
import { ConnectedCaseMinDto } from 'types/matters';
import { errorAlert } from 'utils/alert';
import { getClassifierText, t } from 'utils/getTranslations';
import { ENTITY_LINK_CLASS, getStatusChipType } from '../util';

interface ConnectedCaseLinkProps {
    connectedCase: ConnectedCaseMinDto;
    matterStateTypes: ClassifierData | undefined;
}

interface Props extends ConnectedCaseLinkProps {
    offerCaseId: number;
    isUnlinkEnabled: boolean;
}

interface UnlinkButtonProps {
    offerCaseId: number;
    connectedCaseId: number;
}

function UnlinkConnectedCaseButton({ offerCaseId, connectedCaseId }: UnlinkButtonProps): JSX.Element {
    const { openModal, closeModal } = useModal();
    const [unlinkConnectedCase, { isLoading }] = useUnlinkConnectedCaseMutation();

    const confirmUnlink = useCallback(() => {
        openModal(
            <ConfirmationModal
                titleId="matters.unlink-case"
                confirmId="matters.unlink-case"
                onCancel={closeModal}
                onConfirm={() => {
                    void unlinkConnectedCase({ offerCaseId, connectedCaseId })
                        .unwrap()
                        .then(closeModal)
                        .catch((e) => {
                            console.error(e);
                            errorAlert(t('forms.error-submitting-the-form'));
                        });
                }}
                content={<p className="text-body1">{t('matters.unlink-case-warning')}</p>}
            />,
            closeModal,
            { width: 480 }
        );
    }, [openModal, closeModal, unlinkConnectedCase, offerCaseId, connectedCaseId]);

    return (
        <Tooltip content={t('matters.unlink-case')}>
            <span>
                <IconButton
                    aria-label={t('matters.unlink-case')}
                    size="small"
                    disabled={isLoading}
                    onClick={confirmUnlink}
                >
                    <LinkOffIcon fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );
}

export function ConnectedCaseLink({ connectedCase, matterStateTypes }: ConnectedCaseLinkProps): JSX.Element {
    return (
        <span className="inline-flex flex-wrap items-center gap-1">
            <RecordLink className={ENTITY_LINK_CLASS} to={`/matter/${connectedCase.id}`} label={connectedCase.name} />
            <StatusChip
                type={getStatusChipType(connectedCase.matterStateTypeCode)}
                label={getClassifierText(matterStateTypes, connectedCase.matterStateTypeCode)}
            />
        </span>
    );
}

// Unlinking is only ever initiated from the Offer Won case, so `offerCaseId` is that case and `connectedCase` is the
// case that was opened from it.
export function ConnectedCase({ offerCaseId, connectedCase, matterStateTypes, isUnlinkEnabled }: Props): JSX.Element {
    return (
        <span className="inline-flex flex-wrap items-center gap-1">
            <ConnectedCaseLink connectedCase={connectedCase} matterStateTypes={matterStateTypes} />
            {isUnlinkEnabled ? (
                <UnlinkConnectedCaseButton offerCaseId={offerCaseId} connectedCaseId={connectedCase.id} />
            ) : null}
        </span>
    );
}
